import { useCallback, useMemo } from "react";

import type { FormStore } from "../../store/types";
import { useFormStoreContext } from "../../store/zustand-context";
import type { AttributeCollectionId } from "../../types";

import { useForm } from "./";

interface UseDynamicCollectionsResult {
  /** コレクションインデックスの配列 */
  collectionIndices: number[];
  /** 新しいコレクションを追加できるかどうか */
  canAddCollection: boolean;
  /** コレクション追加ハンドラー */
  handleAddCollection: () => void;
  /** コレクション削除ハンドラー */
  handleRemoveCollection: (index: number) => void;
  /** コレクションの有効/無効を切り替える関数 */
  toggleCollectionEnabled: (index: number, enabled: boolean) => void;
}

/**
 * 動的コレクションの操作に関するフック
 *
 * 主な責務:
 * - 動的コレクションインデックスの取得
 * - コレクションの追加/削除操作の提供
 * - 入力スキップ状態切り替えの提供
 *
 * @param collectionId コレクションID
 * @returns コレクション管理に関する操作メソッド
 */
export function useDynamicCollections(
  collectionId: AttributeCollectionId,
): UseDynamicCollectionsResult {
  const store: FormStore = useFormStoreContext();
  const form = useForm();
  const { getDynamicCollections } = form;
  const dynamicCollections = getDynamicCollections?.();

  // コレクションインスタンスのインデックス配列を取得
  const collectionIndices = useMemo(() => {
    return store.getCollectionIndices(collectionId);
  }, [store, collectionId]);

  // 削除ハンドラー
  const handleRemoveCollection = useCallback(
    (index: number) => {
      if (dynamicCollections?.removeCollection) {
        dynamicCollections.removeCollection(index);
      } else {
        // ストアの機能を使用
        store.removeCollection(collectionId, index);
      }
    },
    [dynamicCollections, store, collectionId],
  );

  // 追加ハンドラー
  const handleAddCollection = useCallback(() => {
    if (dynamicCollections?.addCollection) {
      const newIndex = dynamicCollections.addCollection();

      // 新しく追加されたコレクションは有効状態にする
      if (newIndex !== undefined) {
        store.setCollectionEnabled(collectionId, newIndex, true);
      }
    } else {
      // ストアの機能を使用
      store.addCollection(collectionId);
    }
  }, [dynamicCollections, store, collectionId]);

  // コレクションの有効/無効を切り替える関数
  const toggleCollectionEnabled = useCallback(
    (index: number, enabled: boolean) => {
      store.setCollectionEnabled(collectionId, index, enabled);
    },
    [store, collectionId],
  );

  return {
    collectionIndices,
    canAddCollection: dynamicCollections?.canAdd ?? false,
    handleAddCollection,
    handleRemoveCollection,
    toggleCollectionEnabled,
  };
}
